import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ANetPayload } from "./authorize.types";
import { NotificationService } from "../../notification/notification.service";
import { LoadingService } from "../../loading.service";

declare global {
  interface Window {
    Accept: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {

  #notificationService = inject(NotificationService);
  #loadingService = inject(LoadingService);

  #APILoginID = environment.apis.authorize.APILoginID;
  #PulicClientKey = environment.apis.authorize.PublicClientkey;

  /** Generous enough that a slow mobile connection is not cut off, short of stranding anyone. */
  #DISPATCH_TIMEOUT_MS = 30_000;
  #SCRIPT_TIMEOUT_MS = 15_000;

  #acceptJsLoad?: Promise<boolean>;

  /**
   * Load Accept.js from the URL this environment specifies, once.
   *
   * It used to be injected from index.html with the production URL hardcoded, which quietly
   * pinned every build — including sandbox ones — to production Authorize.net, since the
   * endpoint lives in the script rather than in the credentials we pass it. Loading it here
   * also retires the old race: index.html deferred the download to idle, so a customer who
   * reached checkout first found `window.Accept` undefined.
   *
   * Call it early via preload() to keep the download off the submit path.
   */
  #ensureAcceptJs(): Promise<boolean> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve(false);
    }

    if (window.Accept?.dispatchData) return Promise.resolve(true);

    this.#acceptJsLoad ??= new Promise<boolean>(resolve => {
      const script = document.createElement('script');
      script.src = environment.apis.authorize.acceptJsUrl;
      script.charset = 'utf-8';
      script.async = true;

      const settle = (loaded: boolean) => {
        clearTimeout(timer);
        // A failed load is not cached: the next attempt gets a fresh try rather than
        // inheriting a transient network error for the rest of the session.
        if (!loaded) this.#acceptJsLoad = undefined;
        resolve(loaded && !!window.Accept?.dispatchData);
      };

      const timer = setTimeout(() => settle(false), this.#SCRIPT_TIMEOUT_MS);
      script.onload = () => settle(true);
      script.onerror = () => settle(false);

      document.body.appendChild(script);
    });

    return this.#acceptJsLoad;
  }

  /** Warm Accept.js up while the customer is still filling the form. Safe to call repeatedly. */
  preload(): void {
    this.#ensureAcceptJs();
  }

  async sendPaymentDataToAnet(cardData: ANetPayload, responseHandler: Function) {
    const authData = {
      clientKey: this.#PulicClientKey,
      apiLoginID: this.#APILoginID
    };

    const secureData = {
      authData,
      cardData
    };

    this.#loadingService.open(cardData.cardNumber);

    // Previously this called dispatchData() unguarded. If Accept.js was not there yet it threw
    // `undefined.dispatchData()` after the caller had already raised its processing flag, so
    // the submit button was left spinning and inert with no way back short of a reload.
    if (!await this.#ensureAcceptJs()) {
      this.#loadingService.close(cardData.cardNumber);
      this.#notificationService.show({
        text: 'We could not reach our payment provider. Please check your connection and try again.',
        type: 'error'
      });
      responseHandler(null);
      return;
    }

    // Every exit below has to hand control back exactly once: the caller keeps its processing
    // flag raised — and its submit button spinning — until the handler runs.
    let settled = false;
    // Declared up front: dispatchData is free to invoke its callback synchronously, and settle()
    // would then read the timer handle before its initializer had run.
    let watchdog: ReturnType<typeof setTimeout>;
    const settle = (opaqueData: any) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      this.#loadingService.close(cardData.cardNumber);
      responseHandler(opaqueData);
    };

    // Authorize.net tokenizes inside its own iframe, so a request that hangs there returns no
    // response and throws nothing — invisible to error tracking and, before this, an
    // unrecoverable spinner. Fail it ourselves instead. Tokenization does not charge the card,
    // so retrying after a false alarm is harmless.
    watchdog = setTimeout(() => {
      this.#notificationService.show({
        text: 'Your payment provider did not respond. Please try again.',
        type: 'error'
      });
      settle(null);
    }, this.#DISPATCH_TIMEOUT_MS);

    try {
      window.Accept.dispatchData(secureData, (response: any) => {
        let opaqueData: any = null;

        try {
          if (response?.messages?.resultCode === "Error") {
            for (const message of response.messages.message ?? []) {
              this.#notificationService.show({ text: message.text, type: 'warning' });
            }
          } else {
            opaqueData = response?.opaqueData ?? null;
          }
        } catch (error) {
          // A malformed response used to land here and return without calling the handler,
          // which stranded the caller in the same way the missing script did.
          console.error('ANet Error >>', error);
        }

        settle(opaqueData);
      });
    } catch (error) {
      console.error('ANet dispatch failed >>', error);
      this.#notificationService.show({
        text: 'We could not process your card details. Please try again.',
        type: 'error'
      });
      settle(null);
    }
  }

}
